import { inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAuth, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { ENVIRONMENT_TOKEN } from 'projects/shared/src/lib/core/injection-tokens/environment.token';
import { Environment } from 'projects/shared/src/lib/core/interfaces/environment';
import { SELECTED_LANG } from 'projects/shared/src/lib/core/services/local-storage.service';
import { openIdConfigurationFactory } from 'projects/shared/src/lib/infrastructure/auth/auth-config';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HeorotPreset } from 'projects/heorot/src/assets/heorot-theme';
import { CommonHttpInterceptor } from 'projects/shared/src/lib/infrastructure/interceptors/common-http.interceptor';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';

const getDefaultLanguage = (): string => {
  if (typeof localStorage === 'undefined') {
    return 'nl';
  }

  try {
    const persistedLanguage = JSON.parse(localStorage.getItem(`perm_${SELECTED_LANG}`) ?? 'null');
    return ['en', 'nl', 'nl-be'].includes(persistedLanguage) ? persistedLanguage : 'nl';
  } catch {
    return 'nl';
  }
};

export const appConfig = (environment: Environment) => {
  const defaultLang = getDefaultLanguage();
  return {
    providers: [
      { provide: ENVIRONMENT_TOKEN, useValue: environment },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CommonHttpInterceptor,
        multi: true,
      },
      provideHttpClient(withInterceptorsFromDi()),
      provideRouter(routes, withViewTransitions()),
      provideZonelessChangeDetection(),
      provideAuth(
        {
          config: openIdConfigurationFactory(environment),
        },
        withAppInitializerAuthCheck()
      ),
      provideTranslateService({
        lang: defaultLang,
        fallbackLang: defaultLang,
        loader: provideTranslateHttpLoader({
          failOnError: true,
          useHttpBackend: true,
          resources: [
            { prefix: 'assets/i18n/shared/', suffix: '.json' },
            { prefix: 'assets/i18n/', suffix: '.json' },
            { prefix: 'assets/i18n/tasks-content/', suffix: '.json' },
          ],
        }),
      }),
      provideAppInitializer(() => {
        const translate = inject(TranslateService);

        return firstValueFrom(translate.use(defaultLang));
      }),
      provideAnimationsAsync(),
      providePrimeNG({
        theme: { preset: HeorotPreset, options: { darkModeSelector: '.app-dark' } },
      }),
    ],
  };
}
